import {useState} from 'react';
import {TableCell, TableRow, Stack, Typography, IconButton, Tooltip, CircularProgress} from '@mui/material';
import {styled} from '@mui/material/styles';
import {inputMenuActionType, methodType, FormSelectionEnum, SWRKeyEnum} from 'interfaces';
import {useInputMenuContext} from 'context/InputMenu';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import OutputIcon from '@mui/icons-material/Output';
import UpdateIcon from '@mui/icons-material/Update';
import DownloadIcon from '@mui/icons-material/GetApp';
import {PlanFileForecast} from 'styles/icons';
import DeleteAlert from 'components/alerts/DeleteAlert';
import Alert from 'helpers/Alert';
import useLanguage from 'hooks/useLanguage';
import {useFlags} from 'launchdarkly-react-client-sdk';
import {mutate} from 'swr';
import {EMPTY_ID} from 'hooks/swr';
import backendCommunicator from 'services/backendCommunicator';
import FileDownload from 'js-file-download';
import useIsMounted from 'hooks/useIsMounted';
import {useTranslation} from 'react-i18next';
import {type Plan} from 'client';
import {TagList} from './TagList';
import {useModalWindow} from 'hooks/useModalWindow';
import {PlanPublishModal} from 'components/InputMenu/PlanPublishModal';

export const PlanRow = ({
  plan,
  navigate,
  nested = false,
}: {
  plan: Plan;
  navigate: (planId: string) => void;
  nested: boolean;
}) => {
  const {t} = useTranslation('common');
  const {publishToExternalApi} = useFlags();
  const {openModalWindow} = useModalWindow();
  const {dateFormatter} = useLanguage();
  const [display, setDisplay] = useState<boolean>(false);
  const [exportInProgress, setExportInProgress] = useState<boolean>(false);
  const inputMenuDispatch = useInputMenuContext()?.dispatch;
  const isMounted = useIsMounted();
  const [openDeleteAlert, setOpenDeleteAlert] = useState<boolean>(false);

  function exportPlanHandler() {
    setExportInProgress(true);
    backendCommunicator
      .exportPlan(plan.id)
      .then((response) => {
        setExportInProgress(false);
        isMounted.current && FileDownload(JSON.stringify(response.data), `${plan.name}.json`);
      })
      .catch((error) => {
        setExportInProgress(false);
        isMounted.current && Alert.error(error);
      });
  }

  return (
    <>
      {openDeleteAlert && (
        <DeleteAlert
          open={openDeleteAlert}
          setOpen={setOpenDeleteAlert}
          id={plan.id}
          type={FormSelectionEnum.plan}
          handleCommunicationSucces={() => {
            void mutate([EMPTY_ID, SWRKeyEnum.GET_PLANS]);
          }}
        />
      )}
      <TableRow
        hover
        onMouseEnter={() => {
          setDisplay(true);
        }}
        onMouseLeave={() => {
          setDisplay(false);
        }}
        style={{cursor: 'pointer'}}
      >
        <StyledNameTableCell
          sx={{paddingLeft: nested ? 6 : 2}}
          component="th"
          scope="row"
          onClick={() => {
            navigate(plan.id);
          }}
          data-testid={plan.name}
        >
          <Stack spacing={1} direction={'row'} alignItems={'center'}>
            <PlanFileForecast fontSize="small" color="primary" />
            {/* textfield clamped at 3 rows */}
            <Typography
              sx={{
                display: '-webkit-box',
                overflow: 'hidden',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 3,
              }}
            >
              {plan.name}
            </Typography>
          </Stack>
        </StyledNameTableCell>
        <StyledCell
          onClick={() => {
            navigate(plan.id);
          }}
        >
          {/* textfield clamped at 3 rows */}
          <Typography
            sx={{
              display: '-webkit-box',
              overflow: 'hidden',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 3,
            }}
          >
            {plan.description}
          </Typography>
        </StyledCell>
        <StyledCell
          onClick={() => {
            navigate(plan.id);
          }}
        >
          <Typography noWrap>{dateFormatter.toDateString(plan.startDate)}</Typography>
        </StyledCell>
        <StyledCell
          onClick={() => {
            navigate(plan.id);
          }}
        >
          <Typography noWrap> {dateFormatter.toDateString(plan.endDate)}</Typography>
        </StyledCell>
        <StyledCell
          onClick={() => {
            navigate(plan.id);
          }}
        >
          <Typography noWrap>{plan.updatedAt && dateFormatter.toLimitedTimeAgo(plan.updatedAt)}</Typography>
        </StyledCell>
        <StyledCell
          onClick={() => {
            navigate(plan.id);
          }}
        >
          <Stack spacing={0.5} direction={'row'} alignItems={'center'}>
            {plan?.hasInventoryConnection ? (
              <Tooltip
                title={
                  <span>
                    {t('hasInventoryConnection')}
                    <br />
                    {plan?.lastSynced && dateFormatter.toLimitedTimeAgo(plan.lastSynced)}
                  </span>
                }
              >
                <UpdateIcon fontSize="small" color={plan.lastSynced ? 'secondary' : 'disabled'} />
              </Tooltip>
            ) : (
              <Tooltip title={t('noInventoryConnection') ?? ''}>
                <UpdateIcon fontSize="small" color="disabled" />
              </Tooltip>
            )}
            {publishToExternalApi && plan?.publishedAt ? (
              <Tooltip
                title={
                  <span>
                    {plan.changedSincePublished ? t('hasUnpublishedChanges') : t('hasBeenPublished')}
                    <br />
                    {plan.publishedAt && dateFormatter.toLimitedTimeAgo(plan.publishedAt)}
                  </span>
                }
              >
                <OutputIcon fontSize="small" color={plan.changedSincePublished ? 'primary' : 'secondary'} />
              </Tooltip>
            ) : (
              <Tooltip title={t('hasNotBeenPublished') ?? ''}>
                <OutputIcon fontSize="small" color="disabled" />
              </Tooltip>
            )}
          </Stack>
        </StyledCell>
        <StyledCell
          onClick={() => {
            navigate(plan.id);
          }}
        >
          {plan?.tags && TagList(plan?.tags)}
        </StyledCell>
        <StyledCell sx={{width: 164}} size="small">
          {display && (
            <Stack spacing={1.3} direction={'row'} alignItems={'center'} justifyContent={'right'}>
              <Tooltip title={t('copyAction') ?? ''}>
                <IconButton
                  sx={{padding: 0}}
                  onClick={() =>
                    inputMenuDispatch &&
                    inputMenuDispatch({
                      type: inputMenuActionType.setFormData,
                      form: FormSelectionEnum.plan,
                      method: methodType.copy,
                      formData: {
                        planId: plan.id,
                      },
                    })
                  }
                  size="small"
                  color="secondary"
                >
                  <FileCopyIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('edit') ?? ''}>
                <IconButton
                  sx={{padding: 0}}
                  onClick={() =>
                    inputMenuDispatch &&
                    inputMenuDispatch({
                      type: inputMenuActionType.setFormData,
                      form: FormSelectionEnum.plan,
                      method: methodType.edit,
                      formData: {
                        planId: plan.id,
                      },
                    })
                  }
                  data-testid="plans-table-edit-button"
                  size="small"
                  color="secondary"
                >
                  <EditIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
              {publishToExternalApi && (
                <Tooltip title={t('publishToAPI') ?? ''}>
                  <IconButton
                    sx={{padding: 0}}
                    onClick={() => {
                      openModalWindow(PlanPublishModal, {payload: {id: plan.id}});
                    }}
                    data-testid="plans-table-publish-button"
                    size="small"
                    color="secondary"
                  >
                    <OutputIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title={t('download') ?? ''}>
                {exportInProgress ? (
                  <CircularProgress size={18} />
                ) : (
                  <IconButton
                    sx={{padding: 0}}
                    onClick={() => {
                      exportPlanHandler();
                    }}
                    data-testid="plans-table-download-button"
                    size="small"
                    color="secondary"
                  >
                    <DownloadIcon fontSize="inherit" />
                  </IconButton>
                )}
              </Tooltip>
              <Tooltip title={t('delete') ?? ''}>
                <IconButton
                  sx={{padding: 0}}
                  onClick={() => {
                    setOpenDeleteAlert(true);
                  }}
                  data-testid="plans-table-delete-button"
                  size="small"
                  color="secondary"
                >
                  <DeleteIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        </StyledCell>
      </TableRow>
    </>
  );
};

const StyledNameTableCell = styled(TableCell)(({theme}) => ({
  minWidth: theme.spacing(20),
  height: theme.spacing(5),
  borderBottomColor: theme.palette.divider,
}));

const StyledCell = styled(TableCell)(({theme}) => ({
  borderBottomColor: theme.palette.divider,
  maxWidth: '50vw',
}));
